"use client"

import { useEffect } from "react"
import { useParams, useNavigate } from "react-router-dom"
import useSWR, { mutate } from "swr"
import { ArrowLeft, Phone, AlertCircle, CheckCircle, FileText, Paperclip, Users, X } from "lucide-react"

const fetcher = (url) => fetch(url).then((res) => res.json())

const VendorDetails = () => {
    const { id } = useParams()
    const navigate = useNavigate()
    const url = id ? `/server/dealer_backend/getSpecificVendorLogic?id=${id}` : null
    const { data, isLoading } = useSWR(url, fetcher)

    useEffect(() => {
        if (url) mutate(url)
    }, [url])

    const lead = data?.status === "success" ? data.data[0] : null


    const GridInfoField = ({ label, value, className = "" }) => (
        <div className={`${className}`}>
            <dt className="text-sm font-medium text-gray-600 mb-1">{label}</dt>
            <dd className="text-sm text-gray-900">{value || "—"}</dd>
        </div>
    )

    const handleEditClick = () => {
        navigate(`/edit-vendor/${id}`)
    }


    if (isLoading) {
        return (
            <div className="min-h-screen bg-gray-100 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
                    <p className="text-gray-600 font-medium">Loading vendor details...</p>
                </div>
            </div>
        )
    }

    if (!lead) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center bg-white p-8 rounded-lg shadow border max-w-md">
                    <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
                        <AlertCircle className="w-8 h-8 text-red-500" />
                    </div>
                    <h2 className="text-xl font-semibold text-gray-900 mb-2">Vendor Not Found</h2>
                    <p className="text-gray-600 mb-6">The requested vendor details could not be found.</p>
                    <button
                        onClick={() => navigate(-1)}
                        className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                    >
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Go Back
                    </button>
                </div>
            </div>
        )
    }

    return (
        <div className="min-h-screen bg-gray-100">
            <div className="bg-teal-500 text-white shadow">
                <div className="flex items-center justify-between px-6 py-4">
                    <div className="flex items-center">
                        <button onClick={() => navigate("/Vendor")} className="text-white hover:text-teal-100 transition-colors mr-4">
                            <ArrowLeft className="w-5 h-5" />
                        </button>
                        <div className="w-10 h-10 bg-teal-400 rounded-full flex items-center justify-center text-sm font-semibold mr-3">
                            {lead?.Vendor_Name?.name?.charAt(0) || "?"}
                        </div>
                        <span className="font-medium">{lead?.Vendor_Name?.name}</span>
                    </div>
                    <div className="flex gap-3">
                        <button
                            onClick={handleEditClick}
                            className="px-4 py-2 bg-teal-600 text-white rounded hover:bg-teal-700 transition-colors text-sm"
                        >
                            Edit
                        </button>

                    </div>
                </div>
            </div>

            <div className="flex">


                <div className="flex-1 p-6">
                    <div className="space-y-6">
                        {/* Current State Section */}
                        {/* <div className="bg-white rounded-lg border border-gray-200 p-6">

                            <div className="grid grid-cols-2 gap-6 mt-6">
                                <GridInfoField label="Email ID" value={lead?.Vendor_Email} />
                                <GridInfoField
                                    label="Mobile"
                                    value={
                                        <div className="flex items-center">
                                            <Phone className="w-4 h-4 text-green-600 mr-2" />
                                            {lead.Mobile_No || lead.Mobile_Number}
                                        </div>
                                    }
                                />
                            </div>
                        </div> */}

                        {/* Logistic Request Information Section */}
                        <div className="bg-white rounded-lg border border-gray-200 p-6 ">
                            <h3 className="text-lg font-semibold text-gray-900 mb-6">
                                Logistic Request Information
                            </h3>
                            <div className="grid grid-cols-2 gap-x-12 gap-y-6">
                                <GridInfoField label="Project Name" value={lead?.Customer_Name?.name} />
                                <GridInfoField label="Request Number" value={lead?.Request_Number} />

                                <GridInfoField label="Contact Name" value={lead?.Contact_Name} />
                                <GridInfoField label="Request Type" value={lead?.Request_Type} />
                                <GridInfoField label="Email" value={lead?.Vendor_Email} />

                                <GridInfoField label="Request Against" value={lead?.Request_Against} />

                                <GridInfoField label="Transaction Type" value={lead?.Transaction_Type} />

                                
                                <GridInfoField label="Request Status" value={lead?.Request_Status} />

                                <GridInfoField label="Mobile Number" value={lead?.Mobile_Number} />
                                <GridInfoField label="Priority" value={lead?.Priority} />

                                <GridInfoField label="Due Date" value={lead?.Due_Date} />
                                <GridInfoField label="Remarks" value={lead?.Description} />

                                <GridInfoField label="Pincode" value={lead?.Pincode} />
                                {/* Add more fields as needed in pairs */}
                            </div>
                        </div>


                        {/* Conditional Section */}
                        {lead?.Request_Type === "Pickup" && (
                            <div className="bg-white rounded-lg border border-gray-200 p-6">
                                <h3 className="text-lg font-semibold text-gray-900 mb-6">Pickup Details</h3>
                                <div className="grid grid-cols-2 gap-x-12 gap-y-6">
                                    <GridInfoField label="Pickup Date" value={lead?.Pickup_Date} />
                                    <GridInfoField label="EMP Code" value={lead?.EMP_Code} />
                                </div>
                            </div>
                        )}

                        {lead?.Request_Type === "Dispatch" && (
                            <div className="bg-white rounded-lg border border-gray-200 p-6">
                                <h3 className="text-lg font-semibold text-gray-900 mb-6">Dispatch Details</h3>
                                <div className="grid grid-cols-2 gap-x-12 gap-y-6">
                                    <GridInfoField label="Expected Delivery Date" value={lead?.Expected_Delivery_Date} />
                                </div>
                            </div>
                        )}

                        <div className="bg-white rounded-lg border shadow">
                            <div className="border-b bg-gray-50 px-6 py-4">
                                <h3 className="text-lg font-medium text-gray-900">Serial Number</h3>
                            </div>

                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="text-left py-3 px-6 text-sm font-medium text-gray-700 border-b">Serial No</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {lead?.Serial?.length > 0 ? (
                                            lead.Serial.map((item, index) => (
                                                <tr key={item.id || index} className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                                                    <td className="py-3 px-6 text-sm text-gray-900 border-b">
                                                        {item.Serial_No || "—"}
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td className="py-3 px-6 text-sm text-gray-500 text-center border-b" colSpan={1}>
                                                    No serial numbers available.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>


                        <div className="overflow-x-auto">
                            <div className="border-b bg-gray-50 px-6 py-4">
                                <h3 className="text-lg font-medium text-gray-900">Address Information</h3>
                            </div>
                            <table className="w-full">
                                <thead className="bg-gray-50">
                                    <tr>
                                        {[
                                            "Contact_name",
                                            "Phone_No",
                                            "Unit_Type",
                                            "Pincode",
                                            "Street",
                                            "City",
                                            "State",
                                            "Country"
                                        ].map((key) => (
                                            <th
                                                key={key}
                                                className="text-left py-3 px-6 text-sm font-medium text-gray-700 border-b capitalize"
                                            >
                                                {key.replace(/_/g, " ")}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {lead?.Address_Information?.length > 0 ? (
                                        lead.Address_Information.map((item, index) => (
                                            <tr
                                                key={item.id || index}
                                                className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}
                                            >
                                                {[
                                                    "Contact_name",
                                                    "Phone_No",
                                                    "Unit_Type",
                                                    "Pincode",
                                                    "Street",
                                                    "City",
                                                    "State",
                                                    "Country"
                                                ].map((key) => (
                                                    <td
                                                        key={key}
                                                        className="py-3 px-6 text-sm text-gray-900 border-b"
                                                    >
                                                        {item[key] || "—"}
                                                    </td>
                                                ))}
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td
                                                colSpan={8}
                                                className="py-3 px-6 text-sm text-center text-gray-500 border-b"
                                            >
                                                No address information available.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                    </div>
                </div>
            </div>
        </div>
    )
}

export default VendorDetails
